import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import Marquee from "@/components/Marquee";
import CountUp from "@/components/CountUp";
import FounderVideo from "@/components/FounderVideo";
import { useSeo } from "@/lib/seo";
import { FAQ_LD, breadcrumb } from "@/lib/structuredData";

// Set this to a YouTube video ID (e.g. "dQw4w9WgXcQ") when the founder
// story video is uploaded. Until then the component shows a polished
// "coming soon" placeholder card with the founder portrait as poster.
const FOUNDER_VIDEO_ID = "";

const PILLARS = [
  {
    ix: "01",
    title: "Trust-based partnerships",
    body: "Relationships that compound year on year. We treat each client's business as our own, and each factory's reputation as ours.",
  },
  {
    ix: "02",
    title: "Quality &amp; certainty",
    body: "Products that meet global standards with unwavering consistency — documented in writing, in photos, and on the floor.",
  },
  {
    ix: "03",
    title: "Sustainable growth",
    body: "Empowering India's artisans, small workshops and large producers to thrive in the global market — without sacrificing craft for scale.",
  },
];

const PRINCIPLES = [
  ["Built on relationships, not algorithms", "Trust is the foundation of every partnership."],
  ["Not a commission agent, not a directory", "A genuine partner who understands your brand and protects it."],
  ["Fluency over research", "Deep, personal knowledge of India's manufacturing landscape — built by walking floors."],
  ["Soul-driven craftsmanship", "Honouring the artistry and heritage of India's artisans."],
];

const EXPERTISE = [
  {
    title: "Design & Innovation",
    body: "Working alongside designers and buyers to transform concepts into commercially successful collections — balancing aesthetics, functionality, cost and manufacturability.",
  },
  {
    title: "Engineering & Product Development",
    body: "Technical drawings, CAD reviews, material specifications, structural engineering, product optimisation, compliance requirements and production-ready solutions.",
  },
  {
    title: "Materials & Manufacturing",
    body: "Deep knowledge of brass, aluminium, iron, stainless steel, wood, marble, ceramics, glass, textiles, leather, resin and mixed-material construction — and which process fits which product.",
  },
  {
    title: "Finishing & Surface Development",
    body: "Plating, powder coating, lacquering, polishing, antiquing, hand-finishing, distressed surfaces, painted finishes, electroplating, PVD coatings and patinas.",
  },
  {
    title: "Quality & Compliance",
    body: "Quality assurance systems, factory audits, supplier development, testing coordination, regulatory compliance, packaging validation and shipment inspections.",
  },
  {
    title: "Global Supply Chain Management",
    body: "Sourcing, vendor development, merchandising, production planning, quality control, logistics coordination and export operations for retailers and brands across the US, EU and Middle East.",
  },
];

const TIMELINE = [
  ["2002", "Founded in Moradabad — India's Brass City. First QC desk, first ten clients."],
  ["2008", "Expanded into Jodhpur for furniture and Saharanpur for carved wood."],
  ["2014", "BSCI, Sedex and ISO 9001 audits established as the studio standard."],
  ["2020", "1,000+ factory visits across India. Consolidation hub formalised."],
  ["2025", "23 years in. Eight clusters. Six active export ports."],
];

export const About = () => {
  useSeo({
    title: "About · Dr. Sumit Chhabra",
    description:
      "Asia Sourcing was founded in Moradabad by Dr. Sumit Chhabra — Vice Chairman BAA, Hon. Doctorate (Maryland), NIIFT-trained. 23+ yrs · 1,000+ factories.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        FAQ_LD,
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]),
      ],
    },
    jsonLdId: "about-jsonld",
  });

  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <header className="page-head wrap">
        <Reveal as="p" className="eyebrow">House · About us</Reveal>
        <WordReveal
          as="h1"
          text="A Sourcing & Product Development House, based in Moradabad & Delhi."
          stagger={65}
          testId="about-headline"
        />
        <Reveal as="p" className="lede" delay={200}>
          Twenty-three years inside India's craft and manufacturing clusters. We connect
          global brands with verified Indian factories, managing sourcing, product development,
          quality, compliance and supply chains from concept to shipment.
        </Reveal>
      </header>

      {/* MARQUEE */}
      <Marquee
        testId="about-marquee"
        items={[
          "Moradabad · Brass City",
          "1,000+ factories visited",
          "23 years in",
          "BSCI · Sedex · ISO 9001",
          "FSC · GOTS",
          "6 industry verticals",
          "Built on relationships, not algorithms",
        ]}
      />

      {/* THE COMPANY */}
      <section className="wrap" style={{ paddingBlock: "var(--section)" }}>
        <div className="cap-grid">
          <Reveal>
            <p className="eyebrow">The company</p>
            <h2
              className="display"
              style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}
            >
              Connecting global brands with India's manufacturing excellence.
            </h2>
            <p style={{ color: "var(--ink-soft)", maxWidth: "54ch" }}>
              Asia Sourcing is a sourcing &amp; inspection control company for international
              buyers — founded in Moradabad in 2002, headquartered there ever since. We work
              with European and North-American retail, hospitality, and independent labels
              across <strong>lighting, furniture and home décor</strong>, with deep verticals in
              textiles and leather added since.
            </p>
            <p style={{ color: "var(--ink-soft)", maxWidth: "54ch", marginTop: "1rem" }}>
              Unlike commission agents or directory platforms, we treat each client's business
              as our own. Every order we run is documented from the first sample to the
              container loading photo — because the buyers who hire us, hire us not to be
              surprised.
            </p>
          </Reveal>
          <Reveal style={{ display: "grid", gap: "1.6rem" }}>
            <blockquote
              style={{
                fontFamily: "var(--display)",
                fontStyle: "italic",
                fontWeight: 340,
                fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                lineHeight: 1.35,
                color: "var(--ink)",
                borderLeft: "2px solid var(--brass)",
                padding: ".5rem 0 .5rem 1.4rem",
                margin: 0,
                maxWidth: "44ch",
              }}
              data-testid="about-quote"
            >
              "We don't just source products.<br />
              We source certainty."
              <footer style={{ fontFamily: "var(--mono)", fontStyle: "normal", fontSize: ".7rem", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--brass-deep)", marginTop: "1rem" }}>
                — Asia Sourcing
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="proof wrap" style={{ borderBlock: "1px solid var(--line)" }}>
        <div className="proof-grid">
          <Reveal className="stat"><div className="num"><CountUp target={23} suffix="+" testId="about-stat-years" /></div><div className="lbl">Years on the floor</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={1000} suffix="+" testId="about-stat-factories" /></div><div className="lbl">Factories visited</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={6} testId="about-stat-verticals" /></div><div className="lbl">Industry verticals</div></Reveal>
          <Reveal className="stat"><div className="num"><CountUp target={8} testId="about-stat-clusters" /></div><div className="lbl">Active clusters</div></Reveal>
        </div>
      </section>

      {/* VISION PILLARS */}
      <section className="wrap" style={{ paddingBlock: "var(--section)" }}>
        <Reveal as="p" className="eyebrow">Our vision</Reveal>
        <WordReveal
          as="h2"
          text="Positioning India as the world's next manufacturing house."
          stagger={55}
          className="display"
        />
        <Reveal as="p" style={{ color: "var(--ink-soft)", maxWidth: "60ch", marginTop: "1.4rem" }} delay={150}>
          India doesn't just make things — India makes them with soul. Our vision is built on
          three pillars; the company is built on four principles.
        </Reveal>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
            marginTop: "3rem",
          }}
        >
          {PILLARS.map((p) => (
            <Reveal
              key={p.ix}
              className="specimen"
              style={{ background: "var(--paper-raised)" }}
              data-testid={`pillar-${p.ix}`}
            >
              <span className="ix">Pillar · {p.ix}</span>
              <h3 dangerouslySetInnerHTML={{ __html: p.title }} />
              <p>{p.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOUNDER */}
      <section className="wrap" id="founder" style={{ background: "var(--paper-raised)" }}>
        <div className="founder" data-testid="founder-section" style={{ borderTop: 0 }}>
          <Reveal className="founder-portrait">
            <img
              src="/assets/team/dr-sumit-chhabra.png"
              alt="Dr. Sumit Chhabra, Founder & CEO of Asia Sourcing"
              width="480"
              height="640"
              loading="lazy"
            />
            <span className="seal">Moradabad · Brass City</span>
          </Reveal>
          <Reveal className="founder-bio">
            <p className="eyebrow">The founder</p>
            <h2>Dr. Sumit Chhabra</h2>
            <p
              className="mono"
              style={{ color: "var(--brass-deep)", marginBottom: "1.2rem" }}
            >
              Founder &amp; CEO · Asia Sourcing India · Vice Chairman, Buying Agents Association (BAA)
            </p>

            <p className="quote">
              "Great products are not sourced. They are engineered, refined, and perfected
              through experience."
            </p>

            <p>
              For more than 23 years, Dr. Sumit Chhabra has been helping some of the world's
              leading retailers and brands transform ideas into commercially successful products.
              His expertise extends far beyond sourcing — it encompasses the complete product
              journey, from concept development and material selection to engineering,
              manufacturing, quality assurance, packaging and global delivery.
            </p>

            <p>
              Awarded an Honorary Doctorate from Maryland University, USA, and professionally
              trained in Fashion Design &amp; Merchandising from NIIFT, Dr. Chhabra combines
              design thinking with deep manufacturing expertise. His ability to bridge creativity
              with engineering enables Asia Sourcing India to develop products that are
              aesthetically refined, technically sound, commercially viable, and ready for
              large-scale production.
            </p>

            <p>
              Unlike conventional sourcing professionals, Dr. Chhabra has spent thousands of
              hours inside factories, workshops, foundries, textile mills and finishing
              facilities across India — personally visiting <strong>more than 1,000
              manufacturing facilities</strong> across Moradabad, Jaipur, Jodhpur, Panipat,
              Firozabad, Surat, Saharanpur, Noida and Kanpur. He believes exceptional products
              are created by understanding every manufacturing process firsthand.
            </p>

            <p>
              In addition to leading Asia Sourcing India, Dr. Chhabra serves as
              <strong> Vice Chairman of the Buying Agents Association (BAA)</strong>, actively
              contributing to the growth of India's sourcing and export ecosystem while
              promoting ethical, transparent and sustainable manufacturing practices.
            </p>

            <div className="founder-meta">
              <div><span>Experience</span><strong>23+ years</strong></div>
              <div><span>Factories visited</span><strong>1,000+</strong></div>
              <div><span>Education</span><strong>Hon. Doctorate (Maryland) · NIIFT</strong></div>
              <div><span>Industry role</span><strong>Vice Chairman, BAA</strong></div>
              <div><span>Base</span><strong>Moradabad &amp; Delhi, India</strong></div>
              <div><span>Markets</span><strong>USA · EU · Middle East</strong></div>
            </div>

            <div style={{ marginTop: "1.8rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a
                href="https://www.linkedin.com/in/dr-sumit-chhabra-158a1b28"
                target="_blank"
                rel="noreferrer"
                className="btn btn-ghost"
                style={{ paddingLeft: 0 }}
                data-testid="founder-linkedin"
              >
                LinkedIn ↗
              </a>
              <Link to="/contact" className="btn btn-fill" data-testid="founder-contact">
                Write to the founder
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER VIDEO — swap FOUNDER_VIDEO_ID at top of file to activate */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="founder-video-section"
      >
        <Reveal>
          <p className="eyebrow" style={{ marginBottom: ".8rem" }}>
            In his own words
          </p>
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)",
              margin: "0 0 1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
              maxWidth: "36ch",
            }}
          >
            Twenty-three years on the factory floor — a founder's account.
          </h2>
          <p
            style={{
              color: "var(--ink-soft)",
              fontSize: "1.02rem",
              lineHeight: 1.7,
              maxWidth: "62ch",
              margin: "0 0 1.8rem",
            }}
          >
            Dr. Sumit Chhabra on the discipline of walking a thousand factory floors — what changes when a
            sourcing partner refuses commissions, why Moradabad is still where every serious brass programme
            begins, and how a buying agent's job actually gets done.
          </p>
        </Reveal>
        <Reveal>
          <FounderVideo
            videoId={FOUNDER_VIDEO_ID}
            posterSrc="/assets/team/dr-sumit-chhabra.png"
            posterAlt="Dr. Sumit Chhabra, Founder &amp; CEO of Asia Sourcing"
            title="The founder's story · 23 years on the factory floor"
          />
        </Reveal>
      </section>

      {/* FOUNDER EXPERTISE */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="founder-expertise"
      >
        <Reveal as="p" className="eyebrow">The founder · Expertise</Reveal>
        <WordReveal
          as="h2"
          text="The complete product-development lifecycle."
          stagger={55}
          className="display"
        />
        <Reveal as="p" style={{ color: "var(--ink-soft)", maxWidth: "62ch", marginTop: "1.2rem" }} delay={120}>
          What Dr. Chhabra personally owns on every brief — six disciplines that turn
          a concept into a container-ready product.
        </Reveal>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.4rem",
            marginTop: "2.6rem",
          }}
        >
          {EXPERTISE.map((e, i) => (
            <Reveal
              key={e.title}
              className="specimen"
              style={{ background: "var(--paper)" }}
              data-testid={`expertise-${i}`}
            >
              <span className="ix">/ {String(i + 1).padStart(2, "0")}</span>
              <h3>{e.title}</h3>
              <p>{e.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="wrap" style={{ paddingBlock: "var(--section)" }}>
        <Reveal as="p" className="eyebrow">Core principles</Reveal>
        <WordReveal
          as="h2"
          text="How the house actually operates."
          stagger={60}
          className="display"
        />
        <ul
          className="cap-list"
          style={{ marginTop: "2.4rem", maxWidth: "880px" }}
          data-testid="principles-list"
        >
          {PRINCIPLES.map(([k, v]) => (
            <Reveal as="li" key={k} style={{ display: "block", padding: "1.4rem 0", borderBottom: "1px solid var(--line)" }}>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.3rem",
                  letterSpacing: "-.01em",
                  margin: 0,
                }}
              >
                {k}
              </p>
              <p style={{ color: "var(--ink-soft)", marginTop: ".4rem", maxWidth: "60ch" }}>{v}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* TIMELINE */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Milestones</Reveal>
        <WordReveal as="h2" text="Twenty-three years, in five marks." stagger={60} className="display" />
        <ol
          style={{
            listStyle: "none",
            padding: 0,
            margin: "2.4rem 0 0",
            borderLeft: "1px solid var(--line)",
          }}
          data-testid="timeline"
        >
          {TIMELINE.map(([y, t]) => (
            <Reveal
              as="li"
              key={y}
              style={{
                position: "relative",
                paddingLeft: "2.4rem",
                paddingBlock: "1.4rem",
                borderBottom: "1px solid var(--line)",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "-7px",
                  top: "1.9rem",
                  width: 13,
                  height: 13,
                  background: "var(--brass)",
                  borderRadius: "50%",
                  boxShadow: "0 0 0 4px var(--paper)",
                }}
              />
              <span
                className="mono"
                style={{ color: "var(--brass-deep)", fontSize: ".82rem" }}
              >
                {y}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.25rem",
                  marginTop: ".35rem",
                  letterSpacing: "-.01em",
                  maxWidth: "60ch",
                }}
              >
                {t}
              </p>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* HOUSE FIGURES */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <div className="cap-grid">
          <Reveal>
            <p className="eyebrow">The house, in figures</p>
            <h2
              className="display"
              style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.2rem", lineHeight: 1.05 }}
            >
              The address, the floor, the certificates.
            </h2>
            <p style={{ color: "var(--ink-soft)", maxWidth: "54ch" }}>
              We are deliberately category-focused: lighting, furniture, home décor — with
              expanded depth in textiles and leather. Outside that, we say no.
            </p>
          </Reveal>
          <Reveal>
            <div style={{ display: "grid", gap: "1.4rem" }}>
              {[
                ["Founded", "2002 · Moradabad, India"],
                ["Base", "D-8 Manarover Colony, Delhi Road, Moradabad 244001"],
                ["Phone", "+91 90123 02222"],
                ["Email", "Hello@asiasourcing.co.in"],
                ["Field staff", "12 across 8 clusters"],
                ["QC inspectors", "5 in-house, AQL-trained"],
                ["Certifications", "BSCI · Sedex · ISO 9001 · FSC · GOTS"],
              ].map(([k, v]) => (
                <div key={k} style={{ borderTop: "1px solid var(--line)", paddingTop: ".9rem" }}>
                  <span className="mono" style={{ color: "var(--brass-deep)" }}>{k}</span>
                  <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".3rem 0 0" }}>{v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="cta wrap">
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          Twenty-three years in.<br />Still answering email personally.
        </Reveal>
        <Reveal className="row">
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="about-cta">
            <span>Send an enquiry</span>
          </Link>
          <Link to="/work" className="btn btn-ghost">See the work</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default About;
